import { DeclarationBlockProps } from '../components/DeclarationBlock'
import { Icons } from '../components/Icon'
import {
  Child,
  DeclarationItem,
  DeclarationItemType,
  EstablishmentType,
  Partner,
} from '../graphql/generated/graphql'
import { DeclarationItemState, FullDeclaration } from '../types'

export const formatDeclaration = (t: any, declaration: { [key: string]: any }): string => {
  return Object.keys(declaration)
    .map((key) => {
      if (!(declaration as any)[key]) return ''
      if (key === 'id') return ''
      return `**${t(`declaration.description.${key}`)}** : ${(declaration as any)[key]}`
    })
    .filter((item) => item !== '')
    .join(' ; ')
}

export const formatDeclarationItem = (t: any, declaration: DeclarationItem): string => {
  const fullDeclaration: Omit<FullDeclaration, 'state'> = {
    companyJob: declaration.function,
    id: parseInt(declaration.id),
    type: t(`declaration.type.${declaration.type.toLowerCase()}`),
    // Affichage de entity pour Mandate ou ForeignCompany
    ...(declaration.type === DeclarationItemType.Mandate ||
    declaration.type === DeclarationItemType.ForeignCompany
      ? {
          entity:
            declaration.establishment.entity ?? declaration.establishment.companyName ?? undefined,
        }
      : {}),

    // Affichage de companyName pour Participation ou Function
    ...(declaration.type === DeclarationItemType.Participation ||
    declaration.type === DeclarationItemType.Function
      ? {
          companyName:
            declaration.establishment.companyName ?? declaration.establishment.entity ?? undefined,
        }
      : {}),
    ...(declaration.establishment.siren?.toLowerCase().startsWith('w')
      ? {
          rnaSiren: declaration.establishment.siren,
        }
      : {
          siren: declaration.establishment.siren ?? '',
        }),
    ...(declaration.establishment.city && {
      city: declaration.establishment.city,
    }),
    ...(declaration.establishment.address && {
      address: declaration.establishment.address,
    }),
    ...(declaration.establishment.postalCode && {
      postalCode: declaration.establishment.postalCode,
    }),
  }

  return formatDeclaration(t, fullDeclaration)
}

export const formatMemberForSave = (member: Partner | Child) => {
  return {
    civility: member.civility,
    firstName: member.firstName,
    lastName: member.lastName,
    items: member?.items?.map((item: any) => formatItemForSave(item)),
  }
}

export const formatItemForSave = (item: any) => {
  return {
    function: item.function,
    type: item.type,
    establishment: {
      ...item.establishment,
      companyName: undefined,
      __typename: undefined,
    },
  }
}

export const declarationToFullDeclaration = (
  declaration: DeclarationItem,
  extraParams?: { [key: string]: any }
) => {
  return {
    companyJob: declaration.function,
    id: parseInt(declaration.id),
    type: declaration.type,
    ...(declaration.establishment.companyName && {
      companyName: declaration.establishment.companyName,
    }),
    ...(declaration.establishment.entity && {
      entity: declaration.establishment.entity,
    }),
    ...(declaration.establishment.rna
      ? {
          siren: declaration.establishment.rna,
        }
      : {
          siren: declaration.establishment.siren ?? '',
        }),
    ...(declaration.establishment.city && {
      city: declaration.establishment.city,
    }),
    ...(declaration.establishment.address && {
      address: declaration.establishment.address,
    }),
    ...(declaration.establishment.postalCode && {
      postalCode: declaration.establishment.postalCode,
    }),
    ...extraParams,
  }
}

export const fullDeclarationToDeclaration = (
  declaration: FullDeclaration,
  extraParams?: { [key: string]: any }
): DeclarationItem => {
  return {
    function: declaration.companyJob,
    id: String(declaration.id),
    type: declaration.type,
    establishment: {
      id: String(declaration.id),
      postalCode: declaration.postalCode,
      address: declaration.address,
      city: declaration.city,
      type: EstablishmentType.Company,
      ...(declaration.companyName && {
        companyName: declaration.companyName,
      }),
      ...(declaration.entity && {
        entity: declaration.entity,
        type: EstablishmentType.Entity,
      }),
      ...(declaration.siren && {
        siren: declaration.siren,
      }),
    },
    ...extraParams,
  }
}

export const generateBlock = (
  t: any,
  declaration: {
    title: string
    items: Array<DeclarationItem & { deleteItem?: () => void }> | undefined
    actions?: {
      onDelete: () => void
      onModify: () => void
    }
  },
  type: 'declarant' | 'partner' | 'child'
): DeclarationBlockProps => {
  return {
    icon: {
      icon:
        type === 'declarant' ? Icons.declarant : type === 'partner' ? Icons.conjoint : Icons.enfant,
    },
    title: declaration.title,
    // actions: [
    //   ...(declaration?.actions?.onDelete
    //     ? [
    //         {
    //           text: t('userDeclarations.declarationBlocks.actions.delete'),
    //           variant: ActionButtonVariant.primary,
    //           look: ActionButtonLook.outlined,
    //           ...(declaration?.actions?.onDelete && {
    //             onClick: declaration.actions.onDelete,
    //           }),
    //         },
    //       ]
    //     : []),
    //   ...(declaration?.actions?.onModify
    //     ? [
    //         {
    //           text: t('userDeclarations.declarationBlocks.actions.modify'),
    //           variant: ActionButtonVariant.primary,
    //           look: ActionButtonLook.contained,
    //           ...(declaration?.actions?.onModify && {
    //             onClick: declaration.actions.onModify,
    //           }),
    //         },
    //       ]
    //     : []),
    // ],
    declarationTable: {
      declarations: Array.isArray(declaration.items)
        ? declaration.items.length === 0
          ? [
              {
                id: 1,
                description:
                  type === 'declarant'
                    ? t('declaration.form.declarationListWithoutInterestRegistrant.text')
                    : t('declaration.form.declarationListWithoutInterestOther.text'),
                state: DeclarationItemState.Validated,
              },
            ]
          : declaration.items.map((item, idx) => ({
              id: idx + 1,
              description: formatDeclarationItem(t, item),
              state: DeclarationItemState.Validated,
              // ...(item.deleteItem && {
              //   actions: {
              //     delete: {
              //       variant: ActionButtonVariant.secondary,
              //       look: ActionButtonLook.outlined,
              //       iconProps: {
              //         icon: Icons.trash,
              //       },
              //       onClick: item.deleteItem,
              //     },
              //   },
              // }),
            }))
        : [],
      numberHeaderLabel: t('declarationTable.numberHeaderLabel'),
      descriptionHeaderLabel: t('declarationTable.descriptionHeaderLabel'),
      actionHeaderLabel: t('declarationTable.actionHeaderLabel'),
      stateHeaderLabel: t('declarationTable.stateHeaderLabel'),
      refusedStateLabel: t('declarationTable.refusedStateLabel'),
      validatedStateLabel: t('declarationTable.validatedStateLabel'),
      pendingStateLabel: t('declarationTable.pendingStateLabel'),
      editingStateLabel: t('declarationTable.editingStateLabel'),
    },
  }
}
